import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BottomNav } from './components/nav';
import { StatusBar, UpdateBanner } from './components/status-bar';
import { BigButton } from './components/ui';
import { setCachedFix } from './lib/geo';
import { navigate, useRoute } from './lib/router';
import { pullTrips, startSync } from './lib/sync';
import { DocumentsScreen } from './screens/documents';
import { ExpenseScreen } from './screens/expense';
import { EnrolScreen, PinScreen, UnsupportedScreen } from './screens/login';
import { MessagesScreen } from './screens/messages';
import { MoreScreen } from './screens/more';
import { QueueScreen } from './screens/queue';
import { SettingsScreen } from './screens/settings';
import { SignatureScreen } from './screens/signature';
import { EcmrLoadingScreen } from './screens/ecmr';
import { StopScreen } from './screens/stop';
import { TodayScreen } from './screens/today';
import { VehicleCheckScreen } from './screens/vehicle-check';
import { useSession } from './state/session';
import { applyAppearance, useSettings } from './state/settings';
import { toE7 } from '@kmdx/contracts';

export function App() {
  const { phase, init } = useSession();
  const { theme, textScale, keepScreenAwake, backgroundPosition } = useSettings();

  useEffect(() => {
    void init();
  }, [init]);

  useEffect(() => applyAppearance(theme, textScale), [theme, textScale]);
  useWakeLock(keepScreenAwake);
  useBackgroundPosition(backgroundPosition);

  useEffect(() => {
    if (phase === 'ready') startSync();
  }, [phase]);

  // Изтеглянето на курсовете е единственото четене от сървъра; интервалът е
  // рядък, защото диспечерът и без това праща съобщение при промяна.
  useQuery({
    queryKey: ['trips'],
    queryFn: async () => {
      await pullTrips();
      return Date.now();
    },
    enabled: phase === 'ready',
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });

  if (phase === 'loading') return <Splash />;
  if (phase === 'unsupported') return <UnsupportedScreen />;
  if (phase === 'enrol') return <EnrolScreen />;
  if (phase === 'locked') return <PinScreen />;
  return <Shell />;
}

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper text-2xl font-bold">
      КМД ЕКСПРЕС
    </div>
  );
}

const TAB_ROUTES = ['/', '/expense', '/messages', '/more'];

function Shell() {
  const { path, segments } = useRoute();

  // Екранът „Часове" е премахнат. Стар адрес към него (историята, презареждане
  // след обновяване) води към „Днес" — иначе „Днес" се показва без долното меню.
  useEffect(() => {
    if (path === '/hours') navigate('/', true);
  }, [path]);

  return (
    <div className="flex min-h-dvh flex-col bg-paper">
      <UpdateBanner />
      <StatusBar />
      <InstallPrompt />
      <div className="flex-1">{renderRoute(path, segments)}</div>
      {TAB_ROUTES.includes(path) ? <BottomNav /> : null}
    </div>
  );
}

function renderRoute(path: string, segments: string[]) {
  if (segments[0] === 'stop' && segments[1]) {
    if (segments[2] === 'docs') return <DocumentsScreen stopId={segments[1]} />;
    if (segments[2] === 'sign') return <SignatureScreen stopId={segments[1]} />;
    if (segments[2] === 'ecmr') return <EcmrLoadingScreen stopId={segments[1]} />;
    return <StopScreen stopId={segments[1]} />;
  }
  switch (path) {
    case '/expense':
      return <ExpenseScreen />;
    case '/messages':
      return <MessagesScreen />;
    case '/more':
      return <MoreScreen />;
    case '/check':
      return <VehicleCheckScreen />;
    case '/settings':
      return <SettingsScreen />;
    case '/queue':
      return <QueueScreen />;
    default:
      return <TodayScreen />;
  }
}

/** Инсталиране на началния екран. Без него iOS не праща известия изобщо. */
function InstallPrompt() {
  const [event, setEvent] = useState<Event | null>(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('kmdx.driver.installDismissed') === '1',
  );

  useEffect(() => {
    const handler = (incoming: Event) => {
      incoming.preventDefault();
      setEvent(incoming);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!event || dismissed) return null;

  return (
    <div className="space-y-2 border-b-2 border-line bg-card p-3">
      <p className="text-lg font-semibold">
        Добавете приложението на началния екран — така се отваря с едно докосване и работи и без
        интернет.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <BigButton
          tone="brand"
          onClick={() => {
            const promptable = event as Event & { prompt?: () => Promise<void> };
            void promptable.prompt?.();
            setEvent(null);
          }}
        >
          Добави
        </BigButton>
        <BigButton
          tone="plain"
          onClick={() => {
            localStorage.setItem('kmdx.driver.installDismissed', '1');
            setDismissed(true);
          }}
        >
          Не сега
        </BigButton>
      </div>
    </div>
  );
}

/** Екранът в стойката не бива да гасне по средата на адрес. */
function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const wakeLock = (
      navigator as Navigator & {
        wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
      }
    ).wakeLock;
    if (!wakeLock) return;

    let sentinel: { release: () => Promise<void> } | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const lock = await wakeLock.request('screen');
        if (cancelled) void lock.release();
        else sentinel = lock;
      } catch {
        // Батерията е ниска или браузърът отказва — не е причина за грешка.
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', acquire);
      void sentinel?.release();
    };
  }, [enabled]);
}

/**
 * Незадължителният шев за позиция във фонов режим. По подразбиране е изключен
 * (виж „Настройки"): източникът на позиция е бордовото устройство на буса.
 * Когато шофьорът го включи изрично, приложението държи една прясна точка,
 * която служи САМО ако GPS-ът не отговори в момента на натискане на бутон.
 */
function useBackgroundPosition(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !('geolocation' in navigator)) return;
    let lastAt = 0;
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const now = Date.now();
        if (now - lastAt < 5 * 60_000) return;
        lastAt = now;
        setCachedFix({
          latE7: toE7(position.coords.latitude),
          lonE7: toE7(position.coords.longitude),
          accuracyM: Math.round(position.coords.accuracy),
        });
      },
      () => undefined,
      { enableHighAccuracy: false, maximumAge: 300_000, timeout: 60_000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [enabled]);
}
